import React from "react";
import { useState, useEffect, useRef } from "react";
import { IconSvg } from "@att-bit/duc.components.icon-svg";
import { KeyPress } from "@att-bit/duc.utils.key-press";
import { Calendar } from "./calendar";
import { colorFuncBlue600, colorGray800 } from "@att-bit/duc.styles.tokens";

export type DatePickerInputProps = {
  /** sets the default selected date in date picker */
  selectedDate?: Date;
  /** sets the due date in date picker */
  dueDate?: Date;
  /** set whether a range */
  isRange?: boolean;
  /** sets the start date in date picker in case of range selection */
  startDate?: Date | number;
  /** sets the end date in date picker in case of range selection */
  endDate?: Date | number;
  /** sets whether date picker input is start in case of range selection */
  isStartDate?: boolean;
  /** sets whether date picker input is end in case of range selection */
  isEndDate?: boolean;
  /** array of dates that will be disabled in the date picker */
  disabledDates?: Date[];
  /** set the active start date in date picker, everything before will be disabled */
  activeStartDate?: Date;
  /** set the active end date in date picker, everything after will be disabled */
  activeEndDate?: Date;
  /** set the alert message in calendar */
  alertMessage?: string;

  /** event triggered when a date is selected from primary date picker */
  onSelect?: (val: Date) => void;

  /** sets the label for date picker input */
  labelText?: string;
  /** sets the placeholder for date picker input */
  placeHolderText?: string;
  /** sets the due date legend label */
  dueDateLabel?: string;
  /** sets the selected date legend label */
  selectedDateLabel?: string;
  /** sets the selected due date legend label */
  selectedDueDateLabel?: string;
  /** disables the date picker when set to true */
  disabled?: boolean;
  /** hides the next month icon when reaching the date provided in this props */
  disableNext?: Date;
  /** hides the prev month icon when reaching the date provided in this props */
  disablePrev?: Date;
  /** whether legend at bottom is shown */
  showLegendAtBottom?: boolean;
  /** set true if want to show calender only */
  showCalenderOnly?: boolean;

  /** This will be set as id for date picker input */
  testId?: string;
  /** used for unique identification of element */
  id: string;
  errorMsg?: string;
  tooltip?: any;
};

// for memo: export const DatePickerInput = React.memo(({
export const DatePickerInput = ({ ...props }: DatePickerInputProps) => {
  // HOOKS ##############################################################

  /* useRef */
  const didMountRef = useRef(false);
  const datePickerInputRef = useRef(null);
  const showCalendarRef = useRef(false);
  const buttonRef: React.RefObject<HTMLButtonElement> = React.createRef();

  /* stateful items */
  const [showCalendar, setShowCalendar] = useState(false);
  const [touched, setTouched] = useState(false);

  /* asynchronous after render with cleanup - similar to componentDidMount, but not the same since layout is not available */
  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    if (didMountRef.current) {
      if (!showCalendar) {
        buttonRef.current?.focus();
      }
    } else {
      didMountRef.current = true;
    }
  }, [showCalendar]);

  // METHODS #############################################################

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (KeyPress.enterIsPressed(e) || KeyPress.spaceIsPressed(e)) {
      e.preventDefault();
      displayCalendar();
    } else if (KeyPress.escIsPressed(e) && showCalendarRef.current) {
      hideCalendar();
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      datePickerInputRef.current &&
      !datePickerInputRef.current.contains(event.target) &&
      showCalendarRef.current
    ) {
      hideCalendar();
    }
  };

  const displayCalendar = () => {
    if (showCalendar) {
      hideCalendar();
    } else {
      setShowCalendar(true);
      showCalendarRef.current = true;
    }
  };

  const hideCalendar = (selected?: boolean) => {
    if (!selected) {
      setTouched(true);
    } else {
      setTouched(false);
    }
    setShowCalendar(false);
    showCalendarRef.current = false;
  };

  const onSelect = (date: Date) => {
    if (props.onSelect) {
      props.onSelect(date);
    }
    hideCalendar(true);
  };

  const formatDate = () => {
    let formattedDate: string;
    const date = props.selectedDate;
    if (typeof date === "object") {
      formattedDate =
        (date.getMonth() < 9 ? "0" : "") +
        (date.getMonth() + 1) +
        "/" +
        (date.getDate() <= 9 ? "0" : "") +
        date.getDate() +
        "/" +
        date.getFullYear();
    }
    return formattedDate;
  };

  const error =
    props.startDate !== -1 &&
    props.endDate !== -1 &&
    props.startDate > props.endDate
      ? props.isStartDate
        ? `Start date cannot be greater than end date`
        : `End date cannot be lesser than start date`
      : touched && !props.selectedDate
      ? props.isRange
        ? props.isStartDate
          ? `Please select a start date`
          : `Please select an end date`
        : `Please enter a date`
      : ``;

  const selectedStringDate =
    typeof props.selectedDate === "object" ? formatDate() : "mm/dd/yyyy";

  // RENDER ##############################################################

  return (
    <>
      <div ref={datePickerInputRef}>
        <div className="form-row">
          <div className="inline-flex">
          <label
            id={`date-label-${props.id}`}
            className={`formfield-label ${props.disabled ? "disabled" : ""} ${
              (props.errorMsg || error.length > 0) ? "error" : ""
            }`}
          >
            {props.labelText}
          </label>
          {props.tooltip ? props.tooltip : ""}
          </div>
          <div className="flex flex-column justify-left rel">
            <button
              className="btn-reset absolute top left width-full height-lg-all transparent"
              disabled={props.disabled}
              aria-expanded={showCalendar}
              aria-labelledby={`date-label-${props.id}`}
              aria-describedby={`datepicker_span-${props.id} ${
                (props.errorMsg || error?.length > 0) ? `error-msg-${props.id}` : ``
              }`}
              onClick={displayCalendar}
              onKeyDown={handleKeyDown}
              ref={buttonRef}
              role={"button"}
            >
              <span
                id={`datepicker_span-${props.id}`}
                className="hidden-spoken"
              >
                The date you selected is {selectedStringDate}. Activate to open
                calendar and select a date to close. Dates in the past are not
                selectable
              </span>
            </button>
            <span
              className={`calendar-icon absolute top left height-sm-all width-sm-all line-h-0 ${
                props.disabled ? "color-gray-500" : "color-gray-700"
              }`}
            >
              <IconSvg
                aria-hidden={true}
                aria-label="Calendar icon"
                svgNodeStr={`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><path
                                            class="svg-base" d="M26 4h-3V3h-2v1H11V3H9v1H6a3 3 0 00-3 3v19a3 3 0 003 3h20a3 3 0
                                            003-3V7a3 3 0 00-3-3zM6 6h3v1h2V6h10v1h2V6h3a1 1 0 011 1v2H5V7a1 1 0 011-1zm20 21H6a1 1 0
                                            01-1-1V11h22v15a1 1 0 01-1 1zm-4-14h2v2h-2zM8 13h2v2H8zm7 0h2v2h-2zm7 5h2v2h-2zM8
                                            18h2v2H8zm7 0h2v2h-2zm-7 5h2v2H8zm7 0h2v2h-2z"/></svg>`}
              />
            </span>
            <input
              id={`calendar-input-${props.testId}`}
              name={`calendar-input-${props.testId}`}
              type="text"
              tabIndex={-1}
              className={`textfield pad-r-xxs ${
                props.disabled ? "disabled" : ""
              } ${(props.errorMsg || error.length > 0) ? "error" : ""}`}
              style={{ paddingLeft: "40px" }}
              aria-hidden={true}
              placeholder={props.placeHolderText}
              aria-invalid={error?.length > 0 ? true : undefined}
              disabled={props.disabled}
              readOnly={true}
              value={`${
                typeof props.selectedDate === "object"
                  ? formatDate()
                  : typeof props.selectedDate === "string"
                  ? props.selectedDate
                  : ""
              }`}
            />
            {(props.errorMsg || error.length > 0) && (
              <div
                className={"formfield-msg bg-ui-white"}
                id={`error-msg-${props.id}`}
                aria-hidden="true"
                role="alert"
                tabIndex={-1}
              >
                {props.errorMsg ||error}
              </div>
            )}
          </div>
        </div>
        <Calendar
          testId={props.testId}
          id={props.id}
          selectedDate={props.selectedDate}
          dueDate={props.dueDate}
          isRange={props.isRange}
          startDate={props.startDate}
          isStartDate={props.isStartDate}
          isEndDate={props.isEndDate}
          endDate={props.endDate}
          alertMessage={props.alertMessage}
          disabledDates={props.disabledDates}
          activeStartDate={props.activeStartDate}
          activeEndDate={props.activeEndDate}
          disableNext={props.disableNext}
          disablePrev={props.disablePrev}
          showLegendAtBottom={props.showLegendAtBottom}
          labelText={props.labelText}
          dueDateLabel={props.dueDateLabel}
          selectedDateLabel={props.selectedDateLabel}
          selectedDueDateLabel={props.selectedDueDateLabel}
          onSelect={onSelect}
          hideCalendar={hideCalendar}
          showCalendar={showCalendar}
          showCalenderOnly={props.showCalenderOnly}
        />
      </div>
      <style jsx>{`
        :global(.is-tabbing button[aria-expanded]) {
          outline: none !important;
        }

        @media (max-width: 320px) {
          :global(.inline-form-row .textfield::placeholder) {
            font-size: 1.3rem;
            letter-spacing: -0.05rem;
          }
          :global(.inline-form-row .textfield) {
            font-size: 1.4rem;
          }
        }

        button[aria-expanded="false"]:hover:not(:focus)
          ~ .textfield:enabled:not([aria-invalid]) {
          border-color: ${colorGray800.value};
        }

        button[aria-expanded="true"] ~ .textfield:not([aria-invalid]),
        button[aria-expanded="true"]:hover ~ .textfield:not([aria-invalid]),
        button[aria-expanded]:focus ~ .textfield:not([aria-invalid]) {
          border-color: ${colorFuncBlue600.value};
        }

        .calendar-icon {
          margin-top: 12px;
          margin-left: 10px;
        }
      `}</style>
    </>
  );
};
